import { Axis, AxisBottom, Orientation } from '@visx/axis'
import { localPoint } from '@visx/event'
import { GridRows } from '@visx/grid'
import { Group } from '@visx/group'
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale'
import { BarStack } from '@visx/shape'
import { SeriesPoint } from '@visx/shape/lib/types'
import { Text } from '@visx/text'
import { useTooltip, defaultStyles, TooltipWithBounds } from '@visx/tooltip'
import { has, uniq } from 'lodash'
import { useMemo, useRef } from 'react'
import { BREAKPOINTS } from 'app/lib/breakpoints'
import { DEFAULT_CHART_HUES, seriesStyle } from 'lib/chartColors'
import CommonLegendOrdinal from '../CommonLegendOrdinal'
import { useLegendOverflow } from './useLegendOverflow'
import Tick from '../Tick'
import { ChipSegment } from './ChipSegment'
import { baseKeysByIndex, capKeysByIndex, keysByTotalDesc, stackTopDown } from 'lib/stackedBars'

type TooltipData = {
  bar: SeriesPoint<DataType>
  key: string
  index: number
  height: number
  width: number
  x: number
  y: number
  color: string
}

type DataType = { day: string; tooltipDisplay?: string; values: { [key: string]: number } }

export type BarStackProps = {
  width: number
  height: number
  data: DataType[]
  allKeys: string[]
  totals?: { [key: string]: number }
  margin?: { top: number; right: number; bottom: number; left: number }
  events?: boolean
  bottomRightSlot?: React.ReactNode
  /** Shown right after the series legend, e.g. the total across every series. */
  legendSuffix?: React.ReactNode
  colorMap?: { [key: string]: string }
  /** Force whole-number y-axis ticks with headroom (for integer count data). */
  integerYAxis?: boolean
  /** Decimal precision for the bar-top labels (default: 0). */
  labelPrecision?: number
  /** Render bar-top labels. Defaults to true for backward compatibility. */
  showBarLabels?: boolean
  /** Thin desktop x-axis labels to fit dense series. Mobile labels are always thinned. */
  thinDesktopLabels?: boolean
  /**
   * Formats series values in the tooltip and the legend totals. Defaults to the raw
   * number, which is what every existing caller renders today; pass one to render
   * currency, units, or a placeholder for empty values.
   */
  valueFormatter?: (value: number) => string
  /** Marks the last bar's period as still in progress (today, this week) with a dashed divider to its left. */
  lastBarInProgress?: boolean
  /** Show legend keys exactly as given instead of splitting camelCase (keeps "GPT", "ElevenLabs"). */
  rawLegendLabels?: boolean
}

export const purple3 = `#a44afe`
export const background = `var(--surface)`
const defaultMargin = { top: 40, right: 0, bottom: 0, left: 0 }
const DEFAULT_VALUE_FORMATTER = (value: number) => `${value}`
const tooltipStyles = {
  ...defaultStyles,
  minWidth: 180,
}

// accessors
const getDate = (d: DataType) => d.day

function round(value, precision) {
  var multiplier = Math.pow(10, precision || 0)
  const rounded = Math.round(value * multiplier) / multiplier
  if (rounded >= 1000) {
    return `${round(rounded / 1000, 1)}k`
  } else if (precision > 0) {
    return rounded.toFixed(precision)
  } else {
    return rounded
  }
}

let tooltipTimeout: number

export default function BarStackGraph({
  width,
  height,
  data,
  totals,
  allKeys,
  bottomRightSlot,
  legendSuffix,
  events = false,
  margin = defaultMargin,
  colorMap,
  integerYAxis = false,
  labelPrecision = 0,
  showBarLabels = true,
  thinDesktopLabels = false,
  valueFormatter = DEFAULT_VALUE_FORMATTER,
  lastBarInProgress = false,
  rawLegendLabels = false,
}: BarStackProps) {
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } = useTooltip<TooltipData>()
  const svgRef = useRef<SVGSVGElement>(null)
  const { legendRef, legendOverflow } = useLegendOverflow()

  // bounds
  const xMax = width
  const yMax = height - margin.top - 100 - legendOverflow

  const keys = useMemo(() => keysByTotalDesc(data), [data])

  const yTotals = useMemo(
    () =>
      data.reduce((allTotals, currentDate) => {
        const total = keys.reduce((dailyTotal, k) => {
          dailyTotal += Number(has(currentDate.values, k) ? currentDate.values[k] : 0)
          return dailyTotal
        }, 0)
        allTotals.push(total)
        return allTotals
      }, [] as number[]),
    [data, keys],
  )

  // scales
  const dateScale = useMemo(
    () =>
      scaleBand<string>({
        domain: data?.map(getDate),
        padding: -0.00888888 * data.length + 0.962,
      }),
    [data],
  )

  // Opt-in whole-number ticks with one step of headroom, so small integer-count
  // series never render fractional axis labels (0.2, 0.4, …).
  const yTickValues = useMemo(() => {
    if (!integerYAxis) return undefined
    const max = Math.max(1, Math.ceil(Math.max(0, ...yTotals)))
    const step = max <= 5 ? 1 : Math.ceil(max / 5)
    const domainMax = Math.ceil((max + 1) / step) * step
    const ticks: number[] = []
    for (let v = 0; v <= domainMax; v += step) ticks.push(v)
    return ticks
  }, [integerYAxis, yTotals])

  const yScale = useMemo(
    () =>
      integerYAxis && yTickValues
        ? scaleLinear<number>({ domain: [0, yTickValues[yTickValues.length - 1]] })
        : scaleLinear<number>({ domain: [0, Math.max(...yTotals)], nice: true }),
    [data, yTotals, integerYAxis, yTickValues],
  )

  const fallbackColorScale = useMemo(
    () =>
      scaleOrdinal<string, string>({
        domain: allKeys,
        range: [...DEFAULT_CHART_HUES],
      }),
    [allKeys],
  )

  // Function to get color with priority to colorMap
  const getColor = useMemo(() => {
    return (key: string) => {
      if (colorMap && colorMap[key]) {
        return colorMap[key]
      }
      return fallbackColorScale(key)
    }
  }, [colorMap, fallbackColorScale])

  dateScale.rangeRound([0, xMax])
  yScale.range([yMax, 0])

  // The in-progress divider sits midway between the last two bars (just left of a lone bar).
  const lastBarX = data.length ? (dateScale(getDate(data[data.length - 1])) ?? 0) : 0
  const inProgressX =
    data.length > 1
      ? ((dateScale(getDate(data[data.length - 2])) ?? 0) + dateScale.bandwidth() + lastBarX) / 2
      : lastBarX - 4

  return !width || !height || width < 10 ? null : (
    <div style={{ position: `relative` }}>
      <svg ref={svgRef} width={width} height={height}>
        {/* `style`, not the `fill` attribute: `background` is `var(--surface)`, which only
            resolves reliably as CSS, not as an SVG presentation attribute. */}
        <rect x={0} y={0} width={width} height={height} style={{ fill: background }} rx={14} />
        {/* `lineStyle`, not `fill`: grid lines have no fill, and `lineStyle` lands in the <line>'s
            `style`, where `var()` resolves reliably (unlike a `stroke` attribute). */}
        <GridRows
          top={margin.top}
          left={margin.left + 50}
          scale={yScale}
          width={xMax}
          height={yMax}
          lineStyle={{ stroke: `var(--line-subtle)` }}
          numTicks={5}
          tickValues={yTickValues}
        />
        <Group top={margin.top}>
          <BarStack<DataType, string>
            data={data}
            keys={keys}
            value={(d, key) => (has(d.values, key) ? d.values[key] : 0)}
            x={getDate}
            xScale={dateScale}
            yScale={yScale}
            color={getColor}
            rx={3}
          >
            {(barStacks) => {
              const capKey = capKeysByIndex(barStacks)
              const baseKey = baseKeysByIndex(barStacks)
              return barStacks?.map((barStack, i) =>
                barStack.bars?.map((bar) => {
                  return (
                    <ChipSegment
                      key={`bar-stack-${barStack.index}-${bar.index}`}
                      x={bar.x}
                      y={bar.y}
                      width={bar.width}
                      height={bar.height}
                      color={bar.color}
                      rounded={capKey[bar.index] === bar.key}
                      base={baseKey[bar.index] === bar.key}
                      onClick={() => {
                        if (events) alert(`clicked: ${JSON.stringify(bar)}`)
                      }}
                      onMouseLeave={() => {
                        tooltipTimeout = window.setTimeout(() => {
                          hideTooltip()
                        }, 300)
                      }}
                      onMouseMove={(event) => {
                        if (tooltipTimeout) clearTimeout(tooltipTimeout)
                        // TooltipInPortal expects coordinates to be relative to containerRef
                        // localPoint returns coordinates relative to the nearest SVG, which
                        // is what containerRef is set to in this example.
                        const coords = localPoint(svgRef.current!, event)
                        const left = bar.x + bar.width / 2

                        showTooltip({
                          tooltipData: bar,
                          tooltipTop: coords?.y,
                          tooltipLeft: left,
                        })
                      }}
                    />
                  )
                }),
              )
            }}
          </BarStack>
        </Group>
        {lastBarInProgress && data.length > 0 && (
          <line
            x1={inProgressX}
            x2={inProgressX}
            y1={margin.top}
            y2={yMax + margin.top}
            style={{ stroke: `var(--fg-minimal)` }}
            strokeWidth={1.5}
            strokeDasharray="4 4"
          />
        )}
        {showBarLabels && (
          <Group top={margin.top} left={margin.left}>
            {data?.map((d) => (
              <Text
                key={`bar-stack-${d.day}`}
                x={(dateScale(d.day) ?? 0) + dateScale.bandwidth() / 2}
                y={yScale(Object.entries(d.values).reduce((sum, value) => sum + Number(value[1]), 0))}
                fontSize={12}
                dy={`-.6em`}
                textAnchor="middle"
                style={{ fontFamily: `Inter`, fill: `var(--fg-strong)` }}
              >
                {`${
                  round(
                    Object.entries(d.values).reduce((sum, value) => sum + Number(value[1]), 0),
                    labelPrecision,
                  ) || ``
                }`}
              </Text>
            ))}
          </Group>
        )}
        <AxisBottom
          top={yMax + margin.top}
          scale={dateScale}
          numTicks={data.length}
          stroke={`d2d2d2`}
          tickStroke={`abcbca`}
          tickLabelProps={(value, i, values) => ({
            angle: -30,
            dx: `0.2em`,
            textAnchor: `end`,
            className: `fill-current text-body-xs text-strong`,
          })}
          hideAxisLine
        >
          {({ ticks }) => {
            // Preserve the shared chart's desktop behavior unless a caller
            // explicitly opts into thinning dense series.
            if (width >= BREAKPOINTS.md && !thinDesktopLabels) return <Tick ticks={ticks} />

            const pxPerLabel = width >= BREAKPOINTS.md ? 70 : 45
            const maxLabels = Math.max(2, Math.floor(width / pxPerLabel))
            if (ticks.length <= maxLabels) return <Tick ticks={ticks} />
            const step = Math.max(1, Math.ceil(ticks.length / maxLabels))
            const shown = ticks.filter((_, i) => i % step === 0 || i === ticks.length - 1)
            return <Tick ticks={shown} />
          }}
        </AxisBottom>

        <Axis
          orientation={Orientation.left}
          top={margin.top}
          left={20}
          scale={yScale}
          tickFormat={(v) => `${v}`}
          stroke={`d2d2d2`}
          tickStroke={`abcbca`}
          numTicks={5}
          tickValues={yTickValues}
          tickLabelProps={{
            className: `fill-current text-body-xs text-subtle`,
          }}
        />
      </svg>
      {/* Wraps on narrow widths; `useLegendOverflow` shrinks the plot when it outgrows its room. */}
      <div
        ref={legendRef}
        style={{
          position: `absolute`,
          bottom: 0,
          width: `100%`,
          display: `flex`,
          flexWrap: `wrap`,
          alignItems: `center`,
          justifyContent: `space-between`,
          rowGap: `4px`,
          fontSize: `14px`,
        }}
      >
        <CommonLegendOrdinal
          scale={fallbackColorScale}
          domain={keys}
          totals={totals}
          getColor={getColor}
          formatTotal={valueFormatter}
          rawLabels={rawLegendLabels}
        />
        {legendSuffix}
        {bottomRightSlot && <div className="ml-auto">{bottomRightSlot}</div>}
      </div>

      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          top={tooltipTop}
          left={tooltipLeft}
          style={tooltipStyles}
          offsetLeft={16}
          offsetTop={12}
          className="graph-tooltip-wrapper"
        >
          <div className="text-ui-xs inverse-text-strong">
            {tooltipData.bar.data.tooltipDisplay ?? tooltipData.bar.data.day}
          </div>
          {stackTopDown(keys, tooltipData.bar.data.values).map(([key, value]) => (
            <div key={key} className="mt-2 flex justify-between">
              <div className="flex items-center">
                <div
                  style={{
                    backgroundColor: seriesStyle(getColor(key)).fill,
                    borderColor: seriesStyle(getColor(key)).stroke,
                  }}
                  className="h-2 w-2 rounded-full border"
                ></div>
                <div className="ml-1.5 text-body-xs inverse-text-default">{key}</div>
              </div>
              <div className="text-body-xs inverse-text-default">{valueFormatter(Number(value))}</div>
            </div>
          ))}
        </TooltipWithBounds>
      )}
    </div>
  )
}
